import { useState, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { SEOHead } from '@/components/SEOHead';
import { Layout } from '@/components/layout/Layout';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import {
  Building2, MapPin, Phone, Mail, Globe, Star, ChevronRight, ChevronLeft,
  MessageCircle, Home as HomeIcon, Map as MapIcon, Building, Share2, ArrowUpRight,
} from 'lucide-react';
import { z } from 'zod';

const inquirySchema = z.object({
  name: z.string().trim().min(1, 'Name required').max(100),
  phone: z.string().trim().min(7, 'Valid phone required').max(20),
  email: z.string().trim().email('Invalid email').max(255),
  subject: z.string().trim().min(1).max(120),
  message: z.string().trim().min(1, 'Message required').max(1000),
});

const TYPE_LABELS: Record<string, string> = {
  house: 'Houses',
  apartment: 'Apartments',
  villa: 'Villas',
  condominium: 'Condominiums',
  studio: 'Studios',
  penthouse: 'Penthouses',
  commercial: 'Commercial',
  office: 'Offices',
  shop: 'Shops',
  warehouse: 'Warehouses',
  building: 'Buildings',
  land: 'Land',
  other: 'Other',
};

const labelFor = (t: string) =>
  TYPE_LABELS[t] || t.charAt(0).toUpperCase() + t.slice(1).replace(/_/g, ' ');

export default function AgencyDetail() {
  const { id } = useParams<{ id: string }>();

  const { data: agency, isLoading } = useQuery({
    queryKey: ['agency', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('agencies').select('*').eq('id', id!).maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  const { data: agents } = useQuery({
    queryKey: ['agency-agents', id],
    queryFn: async () => {
      const { data: agentProfiles } = await supabase.from('agent_profiles').select('*').eq('agency_id', id!);
      if (!agentProfiles || agentProfiles.length === 0) return [];
      const userIds = agentProfiles.map(a => a.user_id);
      const { data: profiles } = await supabase.from('profiles').select('*').in('user_id', userIds);
      const profileMap = Object.fromEntries((profiles || []).map(p => [p.user_id, p]));
      return agentProfiles.map(a => ({ ...a, profile: profileMap[a.user_id] || null }));
    },
    enabled: !!id,
  });

  const agentUserIds = useMemo(() => agents?.map((a: any) => a.user_id) || [], [agents]);

  // All approved/active properties for this agency — for breakdowns and counts
  const { data: agencyProps } = useQuery({
    queryKey: ['agency-properties-full', agentUserIds],
    queryFn: async () => {
      const { data } = await supabase
        .from('properties')
        .select('id, listing_type, property_type')
        .in('user_id', agentUserIds)
        .eq('status', 'active')
        .eq('approval_status', 'approved');
      return data || [];
    },
    enabled: agentUserIds.length > 0,
  });

  const { data: agencyPlots } = useQuery({
    queryKey: ['agency-plots-full', agentUserIds],
    queryFn: async () => {
      const { data } = await supabase
        .from('plots')
        .select('id, listing_type')
        .in('user_id', agentUserIds)
        .eq('status', 'active')
        .eq('approval_status', 'approved');
      return data || [];
    },
    enabled: agentUserIds.length > 0,
  });

  const { data: agencyProjects } = useQuery({
    queryKey: ['agency-projects-count', agentUserIds],
    queryFn: async () => {
      const { data: devs } = await supabase.from('developer_profiles').select('id').in('user_id', agentUserIds);
      if (!devs || devs.length === 0) return 0;
      const devIds = devs.map(d => d.id);
      const { count } = await supabase
        .from('projects')
        .select('id', { count: 'exact', head: true })
        .in('developer_id', devIds)
        .eq('approval_status', 'approved');
      return count || 0;
    },
    enabled: agentUserIds.length > 0,
  });

  const breakdown = useMemo(() => {
    const acc = { sale: {} as Record<string, number>, rent: {} as Record<string, number> };
    (agencyProps || []).forEach((p: any) => {
      const lt = p.listing_type === 'rent' ? 'rent' : 'sale';
      const key = p.property_type || 'other';
      acc[lt][key] = (acc[lt][key] || 0) + 1;
    });
    return acc;
  }, [agencyProps]);

  const saleCount = Object.values(breakdown.sale).reduce((a, b) => a + b, 0);
  const rentCount = Object.values(breakdown.rent).reduce((a, b) => a + b, 0);
  const plotsCount = agencyPlots?.length || 0;
  const projectsCount = agencyProjects || 0;

  if (isLoading) {
    return (
      <Layout>
        <div className="container py-8 space-y-4">
          <Skeleton className="h-32 rounded-xl" />
          <Skeleton className="h-64 rounded-xl" />
        </div>
      </Layout>
    );
  }

  if (!agency) {
    return (
      <Layout>
        <div className="container py-20 text-center">
          <Building2 className="h-14 w-14 mx-auto text-muted-foreground/50 mb-4" />
          <h1 className="text-2xl font-bold">Agency not found</h1>
        </div>
      </Layout>
    );
  }

  const description = typeof agency.description === 'string'
    ? agency.description
    : (agency.description as any)?.en || '';

  const isTitanium = (agency.subscription_tier as string) === 'titanium' || (agency.subscription_tier as string) === 'enterprise';

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: agency.name, url }); } catch {}
    } else {
      navigator.clipboard?.writeText(url);
    }
  };

  return (
    <Layout>
      <SEOHead
        title={`${agency.name} — Real Estate Agency in ${agency.city || 'Ethiopia'}`}
        description={`${agency.name} is a ${agency.is_verified ? 'verified ' : ''}real estate agency${agency.city ? ' in ' + agency.city : ''}. ${saleCount} for sale, ${rentCount} for rent.`}
        canonical={`/agencies/${id}`}
        ogImage={{ type: 'agency', id: id!, badge: agency.is_verified ? 'Verified Agency' : 'Agency' }}
      />

      {/* Breadcrumb */}
      <div className="border-b bg-card">
        <div className="container py-3">
          <nav className="flex items-center gap-2 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/agencies" className="hover:text-foreground transition-colors">Agencies</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-foreground font-medium">{agency.name}</span>
          </nav>
        </div>
      </div>

      <div className="container py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left main column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header card */}
            <div className="flex items-start gap-4 p-4 rounded-xl border bg-card">
              <div className="h-20 w-20 rounded-md border overflow-hidden shrink-0 bg-muted">
                {agency.logo_url ? (
                  <img src={agency.logo_url} alt={agency.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-lg font-bold text-muted-foreground">
                    {agency.name.slice(0, 2).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h1 className="text-xl md:text-2xl font-bold text-foreground break-words">{agency.name}</h1>
                      {isTitanium && (
                        <Badge className="bg-amber-500 text-white border-0 gap-1 text-[10px] tracking-wider font-bold">
                          <Star className="h-3 w-3 fill-current" /> TITANIUM
                        </Badge>
                      )}
                    </div>
                    {agency.city && (
                      <p className="flex items-center gap-1.5 mt-1 text-sm text-muted-foreground">
                        <MapPin className="h-4 w-4" /> {agency.city}{agency.address && `, ${agency.address}`}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-sm text-foreground flex-wrap">
                      <span className="flex items-center gap-1.5">
                        <HomeIcon className="h-4 w-4 text-emerald-600" /> <strong>{saleCount}</strong> for Sale
                      </span>
                      <span className="flex items-center gap-1.5">
                        <MapIcon className="h-4 w-4 text-emerald-600" /> <strong>{rentCount}</strong> for Rent
                      </span>
                      {(agency as any).established_year && (
                        <span className="text-muted-foreground">Est. <strong className="text-foreground">{(agency as any).established_year}</strong></span>
                      )}
                      {(agency as any).employee_count && (
                        <span className="text-muted-foreground"><strong className="text-foreground">{(agency as any).employee_count}+</strong> Staff</span>
                      )}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleShare} className="gap-1.5 shrink-0">
                    <Share2 className="h-3.5 w-3.5" /> Share
                  </Button>
                </div>
              </div>
            </div>

            {/* Properties By Agency */}
            <section>
              <h2 className="text-lg font-bold mb-4">Properties By {agency.name}</h2>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
                <BreakdownGroup
                  icon={HomeIcon}
                  label="Properties for Sale"
                  count={saleCount}
                  viewAllHref={`/buy?agency_id=${id}`}
                  items={breakdown.sale}
                />
                <BreakdownGroup
                  icon={MapIcon}
                  label="Properties for Rent"
                  count={rentCount}
                  viewAllHref={`/rent?agency_id=${id}`}
                  items={breakdown.rent}
                />
                {plotsCount > 0 && (
                  <BreakdownGroup
                    icon={MapIcon}
                    label="Plots & Land"
                    count={plotsCount}
                    viewAllHref={`/plots?agency_id=${id}`}
                    items={(agencyPlots || []).reduce((acc: Record<string, number>, p: any) => {
                      const k = p.listing_type === 'rent' ? 'For Rent' : 'For Sale';
                      acc[k] = (acc[k] || 0) + 1; return acc;
                    }, {})}
                  />
                )}
                {projectsCount > 0 && (
                  <BreakdownGroup
                    icon={Building}
                    label="New Projects"
                    count={projectsCount}
                    viewAllHref={`/projects?agency_id=${id}`}
                    items={{ Active: projectsCount }}
                  />
                )}
              </div>
            </section>

            {/* Agency Staff */}
            <section>
              <h2 className="text-lg font-bold mb-4">Agency Staff</h2>
              {agents && agents.length > 0 ? (
                <StaffCarousel agents={agents} fallbackPhone={agency.phone} fallbackEmail={agency.email} />
              ) : (
                <p className="text-muted-foreground text-sm py-6 text-center bg-muted/30 rounded-lg">
                  No staff members listed yet.
                </p>
              )}
            </section>

            {/* About */}
            <section>
              <h2 className="text-lg font-bold mb-3">About {agency.name}</h2>
              <Card className="rounded-xl">
                <CardContent className="p-5">
                  {description ? (
                    <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{description}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No description provided.</p>
                  )}

                  {(((agency as any).services_offered?.length) || ((agency as any).specializations?.length) || ((agency as any).areas_of_operation?.length)) && (
                    <div className="mt-5 pt-4 border-t space-y-3">
                      {(agency as any).services_offered?.length > 0 && (
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Services</div>
                          <div className="flex flex-wrap gap-1.5">
                            {(agency as any).services_offered.map((s: string) => (
                              <Badge key={s} variant="secondary" className="font-normal">{s}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {(agency as any).specializations?.length > 0 && (
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Specializations</div>
                          <div className="flex flex-wrap gap-1.5">
                            {(agency as any).specializations.map((s: string) => (
                              <Badge key={s} className="bg-emerald-50 text-emerald-700 border-emerald-200 font-normal hover:bg-emerald-100">{s}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {(agency as any).areas_of_operation?.length > 0 && (
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Areas of Operation</div>
                          <div className="flex flex-wrap gap-1.5">
                            {(agency as any).areas_of_operation.map((s: string) => (
                              <Badge key={s} variant="outline" className="font-normal">
                                <MapPin className="h-3 w-3 mr-1" />{s}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid sm:grid-cols-2 gap-4 mt-5 pt-4 border-t text-sm">
                    {(agency as any).manager_name && <InfoRow icon={Building2} label="Manager" value={(agency as any).manager_name} />}
                    {agency.phone && <InfoRow icon={Phone} label="Phone" value={agency.phone} />}
                    {(agency as any).whatsapp && <InfoRow icon={MessageCircle} label="WhatsApp" value={(agency as any).whatsapp} />}
                    {agency.email && <InfoRow icon={Mail} label="Email" value={agency.email} />}
                    {agency.website && <InfoRow icon={Globe} label="Website" value={agency.website} link={agency.website} />}
                    {agency.address && <InfoRow icon={MapPin} label="Address" value={agency.address} />}
                  </div>

                  <SocialRow agency={agency} />
                </CardContent>
              </Card>
            </section>
          </div>

          {/* Sidebar */}
          <div>
            <ContactSidebar agency={agency} />
          </div>
        </div>
      </div>
    </Layout>
  );
}

/* ===== BreakdownGroup ===== */
function BreakdownGroup({
  icon: Icon, label, count, viewAllHref, items,
}: {
  icon: any; label: string; count: number; viewAllHref: string; items: Record<string, number>;
}) {
  const entries = Object.entries(items).sort((a, b) => b[1] - a[1]);
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="h-9 w-9 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
            <Icon className="h-4 w-4" />
          </span>
          <span className="font-semibold text-sm md:text-base text-foreground truncate">
            {count} {label}
          </span>
        </div>
        {count > 0 && (
          <Link to={viewAllHref} className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-0.5 shrink-0">
            View All <ArrowUpRight className="h-3 w-3" />
          </Link>
        )}
      </div>
      {entries.length > 0 ? (
        <div className="grid grid-cols-3 gap-2">
          {entries.map(([type, n]) => (
            <div key={type} className="border rounded-md p-2 text-center bg-card hover:border-emerald-500 transition-colors">
              <div className="text-lg font-bold text-foreground leading-tight">{n}</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wide line-clamp-1">{labelFor(type)}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-xs text-muted-foreground italic py-3">No listings yet.</div>
      )}
    </div>
  );
}

/* ===== Staff carousel ===== */
function StaffCarousel({ agents, fallbackPhone, fallbackEmail }: { agents: any[]; fallbackPhone?: string; fallbackEmail?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const scroll = (dir: 'l' | 'r') => {
    ref.current?.scrollBy({ left: dir === 'l' ? -260 : 260, behavior: 'smooth' });
  };
  return (
    <div className="relative">
      <Button
        variant="outline" size="icon"
        className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full z-10 bg-card shadow"
        onClick={() => scroll('l')}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <Button
        variant="outline" size="icon"
        className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full z-10 bg-card shadow"
        onClick={() => scroll('r')}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
      <div ref={ref} className="flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-2">
        {agents.map((a: any) => (
          <StaffCard key={a.id} agent={a} fallbackPhone={fallbackPhone} fallbackEmail={fallbackEmail} />
        ))}
      </div>
    </div>
  );
}

function StaffCard({ agent, fallbackPhone, fallbackEmail }: { agent: any; fallbackPhone?: string; fallbackEmail?: string }) {
  const name = agent.profile?.full_name || 'Agent';
  const phone = agent.profile?.phone || fallbackPhone;
  const email = fallbackEmail; // emails not on profiles table; fall back to agency email
  return (
    <div className="snap-start shrink-0 w-[200px]">
      <div className="border rounded-lg overflow-hidden bg-card">
        <div className="aspect-square bg-muted/50 flex items-center justify-center bg-[radial-gradient(circle_at_30%_30%,hsl(var(--muted))_0%,hsl(var(--card))_70%)]">
          <Avatar className="h-24 w-24">
            <AvatarImage src={agent.profile?.avatar_url} className="object-cover" />
            <AvatarFallback className="text-xl font-bold">
              {name.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
        </div>
        <div className="p-2.5 text-center">
          <div className="font-semibold text-sm text-foreground truncate uppercase tracking-wide">{name}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-1.5 mt-2">
        <Button
          asChild variant="outline" size="sm"
          className="h-8 gap-1 text-xs"
          disabled={!email}
        >
          <a href={email ? `mailto:${email}` : '#'}><Mail className="h-3 w-3" /> Email</a>
        </Button>
        <Button
          asChild size="sm"
          className="h-8 gap-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
          disabled={!phone}
        >
          <a href={phone ? `tel:${phone}` : '#'}><Phone className="h-3 w-3" /> Call</a>
        </Button>
      </div>
    </div>
  );
}

/* ===== Contact sidebar ===== */
function ContactSidebar({ agency }: { agency: any }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', phone: '', email: '', subject: 'General Inquiry', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = inquirySchema.safeParse(form);
    if (!parsed.success) {
      toast({ title: 'Please check the form', description: parsed.error.issues[0].message, variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.from('leads').insert({
        name: parsed.data.name,
        phone: parsed.data.phone,
        email: parsed.data.email,
        message: `[${parsed.data.subject}] ${parsed.data.message}\n\n— Agency inquiry: ${agency.name}`,
        source: 'agency_page',
        agent_id: agency.created_by || null,
      });
      if (error) throw error;
      toast({ title: 'Inquiry sent', description: `${agency.name} will reach out to you soon.` });
      setForm({ name: '', phone: '', email: '', subject: 'General Inquiry', message: '' });
    } catch (err: any) {
      toast({ title: 'Could not send', description: err.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="rounded-xl border-emerald-200 sticky top-[110px]">
      <CardContent className="p-5 space-y-4">
        {agency.phone && (
          <Button asChild className="w-full bg-emerald-600 hover:bg-emerald-700 text-white gap-2 h-11 text-sm font-semibold">
            <a href={`tel:${agency.phone}`}><Phone className="h-4 w-4" /> Call Now</a>
          </Button>
        )}
        {(agency.phone || (agency as any).whatsapp) && (
          <Button asChild variant="outline" className="w-full gap-2 h-10 border-emerald-600 text-emerald-700 hover:bg-emerald-50">
            <a href={`https://wa.me/${((agency as any).whatsapp || agency.phone).replace(/\D/g, '')}`} target="_blank" rel="noreferrer">
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
          </Button>
        )}

        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <div className="flex-1 h-px bg-border" /> or <div className="flex-1 h-px bg-border" />
        </div>

        <form onSubmit={submit} className="space-y-3">
          <Field label="Your Name" required>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="How should we address you?" />
          </Field>
          <Field label="Phone Number" required>
            <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+251 ..." inputMode="tel" />
          </Field>
          <Field label="Email Address" required>
            <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Your best email address?" />
          </Field>
          <Field label="Subject" required>
            <Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} />
          </Field>
          <Field label="Your Message" required>
            <Textarea
              value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
              placeholder="What would you like to say?" rows={3}
            />
          </Field>
          <Button type="submit" disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-11 font-semibold">
            {submitting ? 'Sending…' : 'Send Your Question'}
          </Button>
          <p className="text-[11px] text-muted-foreground text-center">
            By submitting this form, you agree to <Link to="/terms" className="text-emerald-700 hover:underline">Terms of Use</Link>.
          </p>
        </form>
      </CardContent>
    </Card>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs font-medium text-foreground">
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      {children}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, link }: { icon: any; label: string; value: string; link?: string }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="h-4 w-4 mt-0.5 text-primary shrink-0" />
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        {link ? (
          <a href={link} target="_blank" rel="noreferrer" className="text-foreground font-medium hover:text-primary truncate block">{value}</a>
        ) : (
          <div className="text-foreground font-medium truncate">{value}</div>
        )}
      </div>
    </div>
  );
}

function SocialRow({ agency }: { agency: any }) {
  const socials = [
    { url: agency.social_facebook, label: 'Facebook' },
    { url: agency.social_instagram, label: 'Instagram' },
    { url: agency.social_linkedin, label: 'LinkedIn' },
    { url: agency.social_telegram, label: 'Telegram' },
  ].filter(s => s.url);
  if (socials.length === 0) return null;
  return (
    <div className="mt-5 pt-4 border-t">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Follow on Social</div>
      <div className="flex gap-2">
        {socials.map(s => (
          <a key={s.label} href={s.url} target="_blank" rel="noreferrer"
             className="px-3 py-1.5 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground text-xs font-medium transition-colors">
            {s.label}
          </a>
        ))}
      </div>
    </div>
  );
}
